import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, of, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import { NotificationsController, WindowRef } from '@core/user-interface-legacy';

import { TellentOrganizationHttpClient } from './tellent-organization.http';
import { TellentOrganizationActions } from './tellent-organization.store';
import { TellentCurrentUserEntityActions } from '../../current-user';

@Injectable({ providedIn: 'root' })
export class TellentOrganizationEffects {
  public notificationsController = inject(NotificationsController);
  public i18n = inject(I18nService);

  public effects = createEffectFactory(TellentOrganizationActions, this.actions$);

  public fetch$ = this.effects(TellentOrganizationActions.fetch, () =>
    this.tellentOrganizationHttpClient.fetch(),
  );

  public update$ = this.effects(TellentOrganizationActions.update, ({ payload }) =>
    this.tellentOrganizationHttpClient.update(payload).pipe(
      tap(() =>
        this.notificationsController.success({
          title: this.i18n.translate('tl.team_members.general_settings.save_success'),
        }),
      ),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentOrganizationActions.deleteOrg),
      mergeMap(({ payload }) =>
        this.tellentOrganizationHttpClient.delete(payload.code).pipe(
          switchMap(response => [
            TellentOrganizationActions.deleteOrgSuccess({
              payload: response,
            }),
          ]),
          catchError(error => of(TellentOrganizationActions.deleteOrgFail(error))),
        ),
      ),
    ),
  );

  public addOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentOrganizationActions.addOrg),
      mergeMap(({ payload }) =>
        this.tellentOrganizationHttpClient.addOrg(payload).pipe(
          switchMap(response => [
            TellentOrganizationActions.addOrgSuccess({
              payload: response,
            }),
          ]),
          catchError(error => of(TellentOrganizationActions.addOrgFail(error))),
        ),
      ),
    ),
  );

  public deleteStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentOrganizationActions.deletionStatus),
      switchMap(() =>
        this.tellentOrganizationHttpClient
          .deletionStatus()
          .pipe(
            map(status => TellentOrganizationActions.deletionStatusSuccess({ payload: status })),
          ),
      ),
    ),
  );

  public redirectOnDeleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TellentOrganizationActions.deleteOrgSuccess),
        tap(() => this.windowRef.nativeWindow.location.reload()),
      ),
    {
      dispatch: false,
    },
  );

  constructor(
    private tellentOrganizationHttpClient: TellentOrganizationHttpClient,
    private actions$: Actions,
    private windowRef: WindowRef,
    private store: Store,
  ) {
    this.actions$
      .pipe(ofType(TellentOrganizationActions.addOrgSuccess))
      .subscribe(() => this.store.dispatch(TellentCurrentUserEntityActions.fetch()));
  }
}
